<template>
  <div class="min-h-screen bg-mumble-bg-alt">
    <div class="max-w-7xl mx-auto p-4 md:p-6">
      <!-- Header compatto -->
      <div class="mb-6">
        <div class="flex items-center justify-between gap-4 mb-1">
          <h1 class="text-2xl md:text-3xl font-bold text-mumble-text">Configuratore AMS</h1>
          <img src="/images/logo-mumble-blu.svg" alt="Mumble" class="h-5 md:h-6 shrink-0">
        </div>
        <p class="text-xs md:text-sm text-gray-600">Configura il tuo pacchetto di Application Maintenance & Support</p>
      </div>

      <!-- Layout a 2 colonne -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 pb-32 lg:pb-0">
        <!-- Colonna sinistra: Configurazione -->
        <div class="lg:col-span-2 space-y-6">
          <!-- Package Selection - Compatta -->
          <div class="bg-white rounded-xl p-6">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-xl font-bold text-mumble-text">Seleziona il pacchetto</h2>
              <QuizFinder :packages="packages" @select-package="selectPackage" />
            </div>

            <!-- Tutti i pacchetti in grid -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <!-- Pay as you go -->
              <button
                @click="selectPackage(packages.none)"
                class="text-left p-4 rounded-lg border-2 transition-all duration-200 hover:shadow-lg relative group flex flex-col cursor-pointer"
                :class="[
                  selectedPackage?.name === 'none'
                    ? 'border-mumble-accent bg-mumble-accent/5'
                    : 'border-gray-200 hover:border-mumble-accent/50'
                ]"
              >
                <div v-if="selectedPackage?.name === 'none'" class="absolute -top-2 -right-2 w-6 h-6 bg-mumble-accent rounded-full flex items-center justify-center text-white text-xs font-bold">
                  ✓
                </div>

                <div class="text-xs font-semibold text-gray-500 mb-2">PAY AS YOU GO</div>
                <div class="text-2xl font-bold text-mumble-text mb-2">€100<span class="text-xs text-gray-500 font-normal">/ora</span></div>

                <p class="text-xs text-gray-500 mb-3 leading-relaxed">Interventi sporadici, senza impegno né continuità.</p>

                <div class="space-y-1 text-xs text-gray-600 mb-3">
                  <div class="font-semibold text-gray-500">0 ore incluse</div>
                  <div>Canone: Nessuno</div>
                  <div>Response: <span class="font-medium">—</span></div>
                </div>

                <div class="mt-auto pt-3 border-t border-gray-100 space-y-1 text-xs text-gray-600">
                  <div>✗ Manager dedicato</div>
                  <div>✗ BugReporter</div>
                  <div>✗ AppSentinel</div>
                </div>
              </button>

              <!-- Essential Care -->
              <button
                @click="selectPackage(packages.essential)"
                class="text-left p-4 rounded-lg border-2 transition-all duration-200 hover:shadow-lg relative group flex flex-col cursor-pointer"
                :class="[
                  selectedPackage?.name === 'essential'
                    ? 'border-mumble-accent bg-mumble-accent/5'
                    : 'border-gray-200 hover:border-mumble-accent/50'
                ]"
              >
                <div v-if="selectedPackage?.name === 'essential'" class="absolute -top-2 -right-2 w-6 h-6 bg-mumble-accent rounded-full flex items-center justify-center text-white text-xs font-bold">
                  ✓
                </div>

                <div class="text-xs font-semibold text-mumble-accent mb-2">ESSENTIAL</div>
                <div class="text-2xl font-bold text-mumble-text mb-2">€2.400<span class="text-xs text-gray-500 font-normal">/anno</span></div>

                <p class="text-xs text-gray-500 mb-3 leading-relaxed">Supporto essenziale per assicurare continuità e funzionamento.</p>

                <div class="space-y-1 text-xs text-gray-600 mb-3">
                  <div class="font-semibold text-mumble-accent">8 ore incluse</div>
                  <div>Extra: €85/h</div>
                  <div>Response: <span class="font-medium">16h</span></div>
                </div>

                <div class="mt-auto pt-3 border-t border-gray-100 space-y-1 text-xs text-gray-600">
                  <div>✓ Manager dedicato</div>
                  <div>✓ BugReporter</div>
                  <div>✓ AppSentinel (1 report/anno)</div>
                </div>
              </button>

              <!-- Growth Care -->
              <button
                @click="selectPackage(packages.growth)"
                class="text-left p-4 rounded-lg border-2 transition-all duration-200 hover:shadow-lg relative group flex flex-col cursor-pointer"
                :class="[
                  selectedPackage?.name === 'growth'
                    ? 'border-mumble-accent bg-mumble-accent/5'
                    : 'border-gray-200 hover:border-mumble-accent/50'
                ]"
              >
                <div v-if="selectedPackage?.name === 'growth'" class="absolute -top-2 -right-2 w-6 h-6 bg-mumble-accent rounded-full flex items-center justify-center text-white text-xs font-bold">
                  ✓
                </div>

                <div class="text-xs font-semibold text-mumble-accent mb-2">GROWTH</div>
                <div class="text-2xl font-bold text-mumble-text mb-2">€6.000<span class="text-xs text-gray-500 font-normal">/anno</span></div>

                <p class="text-xs text-gray-500 mb-3 leading-relaxed">Per prodotti che devono crescere ed evolvere continuamente.</p>

                <div class="space-y-1 text-xs text-gray-600 mb-3">
                  <div class="font-semibold text-mumble-accent">40 ore incluse</div>
                  <div>Extra: €80/h</div>
                  <div>Response: <span class="font-medium">8h</span></div>
                </div>

                <div class="mt-auto pt-3 border-t border-gray-100 space-y-1 text-xs text-gray-600">
                  <div>✓ Manager dedicato</div>
                  <div>✓ BugReporter</div>
                  <div>✓ AppSentinel (2 report/anno)</div>
                </div>
              </button>

              <!-- Full Care -->
              <button
                @click="selectPackage(packages.full)"
                class="text-left p-4 rounded-lg border-2 transition-all duration-200 hover:shadow-lg relative group flex flex-col cursor-pointer"
                :class="[
                  selectedPackage?.name === 'full'
                    ? 'border-mumble-accent bg-mumble-accent/5'
                    : 'border-gray-200 hover:border-mumble-accent/50'
                ]"
              >
                <div v-if="selectedPackage?.name === 'full'" class="absolute -top-2 -right-2 w-6 h-6 bg-mumble-accent rounded-full flex items-center justify-center text-white text-xs font-bold">
                  ✓
                </div>

                <div class="text-xs font-semibold text-mumble-navy mb-2">FULL</div>
                <div class="text-2xl font-bold text-mumble-text mb-2">€12.000<span class="text-xs text-gray-500 font-normal">/anno</span></div>

                <p class="text-xs text-gray-500 mb-3 leading-relaxed">Full coverage per business continuity e zero downtime.</p>

                <div class="space-y-1 text-xs text-gray-600 mb-3">
                  <div class="font-semibold text-mumble-accent">80 ore incluse</div>
                  <div>Extra: €75/h</div>
                  <div>Response: <span class="font-medium">4h</span></div>
                </div>

                <div class="mt-auto pt-3 border-t border-gray-100 space-y-1 text-xs text-gray-600">
                  <div>✓ Manager dedicato</div>
                  <div>✓ BugReporter</div>
                  <div>✓ AppSentinel (4 report/anno)</div>
                </div>
              </button>
            </div>
          </div>

          <!-- Slider Section - Compatta -->
          <div
            class="bg-white rounded-xl p-6 transition-opacity duration-200"
            :class="{ 'opacity-50 pointer-events-none': !selectedPackage }"
          >
            <div class="flex items-center justify-between mb-4">
              <div>
                <h3 class="text-lg font-bold text-mumble-text">Ore necessarie all'anno</h3>
                <p v-if="selectedPackage && totalHours > 0" class="text-xs text-gray-500">{{ Math.floor(totalHours / 8) }} giornate lavorative</p>
              </div>
              <div class="text-right flex items-baseline gap-2">
                <input
                  v-if="selectedPackage"
                  type="number"
                  v-model.number="totalHours"
                  :min="selectedPackage?.includedHours || 0"
                  max="480"
                  step="8"
                  inputmode="numeric"
                  class="text-2xl md:text-3xl font-bold text-mumble-accent bg-transparent border-b-2 border-mumble-accent/20 hover:border-mumble-accent focus:border-mumble-accent focus:outline-none w-20 md:w-24 text-right transition-colors touch-manipulation"
                />
                <div v-else class="text-3xl font-bold text-mumble-accent">{{ totalHours }}</div>
                <div class="text-xs text-gray-500">ore</div>
              </div>
            </div>
            
            <div v-if="!selectedPackage" class="text-sm text-gray-500 mb-4">
              Seleziona un pacchetto per configurare le ore
            </div>

            <div v-else class="space-y-4">
              <input
                type="range"
                :min="selectedPackage?.includedHours || 0"
                max="480"
                v-model.number="totalHours"
                step="8"
                class="w-full h-3 md:h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-mumble-accent slider touch-manipulation"
              >
              
              <div class="flex justify-between text-xs text-gray-400">
                <span>{{ selectedPackage?.includedHours || 0 }}</span>
                <span>160</span>
                <span>280</span>
                <span>400</span>
                <span>480</span>
              </div>

              <div v-if="selectedPackage.name !== 'none' && totalHours > selectedPackage.includedHours" class="bg-mumble-accent/5 rounded-lg p-3 flex justify-between items-center">
                <span class="text-sm text-gray-700">Ore extra da acquistare</span>
                <span class="text-xl font-bold text-mumble-accent">+{{ totalHours - selectedPackage.includedHours }}</span>
              </div>
            </div>
          </div>

          <!-- I Plus dei Care -->
          <div class="bg-white rounded-xl p-5 md:p-8 mt-12">
            <h3 class="text-sm font-semibold text-mumble-text/60 uppercase tracking-wider mb-6 md:mb-8">I Plus dei Care</h3>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
              <div>
                <h4 class="text-base font-semibold text-mumble-text mb-3">SLA Garantito</h4>
                <p class="text-sm text-gray-600 leading-relaxed">Response time garantito da 4h a 16h. Le tue richieste ricevono acknowledgement immediato.</p>
                <p class="text-xs text-gray-400 mt-3">Business hours: Lun-Ven 9-18</p>
              </div>

              <div>
                <h4 class="text-base font-semibold text-mumble-text mb-3">Pro Toolkit</h4>
                <p class="text-sm text-gray-600 leading-relaxed">BugReporter e AppSentinel inclusi. Collaboration efficiente e security monitoring continuo.</p>
              </div>

              <div>
                <h4 class="text-base font-semibold text-mumble-text mb-3">Tariffe Agevolate</h4>
                <p class="text-sm text-gray-600 leading-relaxed">Ore extra da €75 a €85/h invece di €100/h. Il risparmio cresce con l'utilizzo.</p>
              </div>
            </div>
          </div>

          <!-- Suite Professionale -->
          <div class="bg-white rounded-xl p-5 md:p-8 mt-12 md:mt-16">
            <h3 class="text-sm font-semibold text-mumble-text/60 uppercase tracking-wider mb-6 md:mb-10">Suite Professionale Inclusa</h3>

            <div class="space-y-8 md:space-y-12">
              <!-- BugReporter -->
              <div class="border-l border-mumble-accent/30 pl-4 md:pl-8">
                <h4 class="text-base font-semibold text-mumble-text mb-4">BugReporter</h4>
                <p class="text-sm text-gray-600 leading-relaxed mb-6">
                  Collaboration platform per issue tracking e feature requests. Segnala bug, richiedi feature e monitora lo stato di avanzamento in tempo reale.
                </p>
                <ul class="space-y-3 text-xs text-gray-600">
                  <li class="leading-relaxed">
                    <span class="font-medium text-mumble-text">Smart Ticketing</span> — Auto-prioritization e assegnazione intelligente al team
                  </li>
                  <li class="leading-relaxed">
                    <span class="font-medium text-mumble-text">Rich Attachments</span> — Upload di file, screenshot e video per documentare le issue
                  </li>
                  <li class="leading-relaxed">
                    <span class="font-medium text-mumble-text">Real-time Notifications</span> — Update istantanei via email e push notification
                  </li>
                  <li class="leading-relaxed">
                    <span class="font-medium text-mumble-text">Analytics Dashboard</span> — Insights su issue risolte e resolution time
                  </li>
                </ul>
              </div>

              <!-- AppSentinel -->
              <div class="border-l border-mumble-navy/30 pl-4 md:pl-8">
                <h4 class="text-base font-semibold text-mumble-text mb-4">AppSentinel</h4>
                <p class="text-sm text-gray-600 leading-relaxed mb-6">
                  Enterprise-grade security monitoring con weekly scans automatiche via SAST e OSV. Le vulnerabilità vengono raccolte in security reports periodici con remediation inclusa.
                </p>
                <ul class="space-y-3 text-xs text-gray-600">
                  <li class="leading-relaxed">
                    <span class="font-medium text-mumble-text">Weekly Scans</span> — Analisi automatica settimanale su tutti i Care plans
                  </li>
                  <li class="leading-relaxed">
                    <span class="font-medium text-mumble-text">SAST (Static Application Security Testing)</span> — Code analysis per vulnerabilità e unsafe patterns
                  </li>
                  <li class="leading-relaxed">
                    <span class="font-medium text-mumble-text">OSV (Open Source Vulnerabilities)</span> — Dependency scanning per CVE e known vulnerabilities
                  </li>
                  <li class="leading-relaxed">
                    <span class="font-medium text-mumble-text">Security Reports & Remediation</span> — Essential: 1/anno · Growth: 2/anno · Full: 4/anno
                  </li>
                  <li class="leading-relaxed">
                    <span class="font-medium text-mumble-text">Included Fixes</span> — Remediation scalata dal monte ore del pacchetto
                  </li>
                </ul>
              </div>
            </div>

            <div class="mt-10 pt-6 border-t border-gray-100">
              <p class="text-xs text-gray-500 leading-relaxed">
                Weekly scans automatiche su tutti i Care plans. Security reports e remediation con frequenza variabile: annuale (Essential), semestrale (Growth) o trimestrale (Full). I fix vengono scalati dalle ore incluse.
              </p>
            </div>
          </div>
        </div>

        <!-- Colonna destra: Summary sempre visibile -->
        <div class="lg:col-span-1">
          <!-- Desktop: normale sticky card -->
          <div class="hidden lg:block bg-white rounded-xl border-2 border-mumble-accent shadow-lg shadow-mumble-accent/10 p-6 sticky top-6">
            <h3 class="text-xl font-bold mb-4 text-mumble-text">Riepilogo</h3>
            
            <div class="space-y-3 text-sm">
              <div class="flex justify-between items-center pb-3 border-b border-gray-100">
                <span class="text-gray-600">Pacchetto</span>
                <span class="font-semibold text-mumble-text">{{ summaryData.packageName }}</span>
              </div>
              
              <div class="flex justify-between items-center pb-3 border-b border-gray-100">
                <span class="text-gray-600">Ore incluse</span>
                <span class="font-semibold text-mumble-accent">{{ summaryData.includedHours }}</span>
              </div>
              
              <div class="flex justify-between items-center pb-3 border-b border-gray-100">
                <span class="text-gray-600">Ore extra</span>
                <span class="font-semibold text-mumble-accent">{{ summaryData.extraHours }}</span>
              </div>
              
              <div class="flex justify-between items-center pb-3 border-b border-gray-100">
                <div>
                  <div class="text-gray-600">Response time</div>
                  <div class="text-xs text-gray-400">(acknowledgement)</div>
                </div>
                <span class="font-semibold text-mumble-accent">{{ summaryData.responseTime }}</span>
              </div>
              
              <div class="pt-2 mt-2">
                <div class="flex justify-between items-center">
                  <span class="text-lg font-bold text-mumble-text">Totale</span>
                  <span class="text-3xl font-bold text-mumble-accent">{{ summaryData.total }}</span>
                </div>
                <div class="text-xs text-gray-500 text-right mt-1">IVA esclusa</div>
              </div>

              <!-- Included Tools indicator -->
              <div v-if="selectedPackage && selectedPackage.name !== 'none'" class="mt-4 pt-4 border-t border-gray-100">
                <div class="text-xs font-semibold text-mumble-text mb-2">Pro Toolkit:</div>
                <div class="flex gap-2">
                  <span class="px-2 py-1 bg-mumble-accent/10 text-mumble-accent text-xs rounded">BugReporter</span>
                  <span class="px-2 py-1 bg-mumble-accent/10 text-mumble-accent text-xs rounded">AppSentinel</span>
                </div>
              </div>

              <!-- Dettaglio piano -->
              <div v-if="selectedPackage" class="mt-4 pt-4 border-t border-gray-100">
                <div class="bg-blue-50 rounded-lg p-3 border border-blue-100">
                  <p class="text-xs text-gray-700 leading-relaxed">
                    <template v-if="selectedPackage.name === 'none'">
                      Questo modello è pensato per interventi occasionali: non include manutenzione preventiva, aggiornamenti programmati, SLA o garanzie sulla stabilità e sul downtime della soluzione.
                    </template>
                    <template v-else-if="selectedPackage.name === 'essential'">
                      Questo piano copre la gestione di base, ma non è indicato per prodotti in crescita né per situazioni che richiedono interventi rapidi.
                    </template>
                    <template v-else-if="selectedPackage.name === 'growth'">
                      Questo piano consente di far evolvere il prodotto, ma non include la gestione strutturata degli incidenti critici o SLA dedicati sul downtime.
                    </template>
                    <template v-else-if="selectedPackage.name === 'full'">
                      Questo piano copre continuità, sicurezza ed evoluzione del prodotto. Sono escluse solo le major release.
                    </template>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Mobile: Bottom bar fixed -->
          <div class="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t-2 border-mumble-accent shadow-2xl z-50">
            <button 
              @click="showMobileSummary = !showMobileSummary"
              class="w-full px-4 py-4 flex items-center justify-between"
            >
              <div class="flex items-center gap-3">
                <span class="text-xs text-gray-600">Totale</span>
                <span class="text-2xl font-bold text-mumble-accent">{{ summaryData.total }}</span>
              </div>
              <svg 
                class="w-5 h-5 text-mumble-accent transition-transform duration-300"
                :class="{ 'rotate-180': showMobileSummary }"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"/>
              </svg>
            </button>
            
            <!-- Dettagli espandibili -->
            <div 
              v-show="showMobileSummary"
              class="px-4 pb-4 border-t border-gray-100"
            >
              <div class="space-y-2 text-sm pt-3">
                <div class="flex justify-between items-center">
                  <span class="text-gray-600">Pacchetto</span>
                  <span class="font-semibold text-mumble-text text-xs">{{ summaryData.packageName }}</span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-gray-600">Ore incluse</span>
                  <span class="font-semibold text-mumble-accent text-xs">{{ summaryData.includedHours }}</span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-gray-600">Ore extra</span>
                  <span class="font-semibold text-mumble-accent text-xs">{{ summaryData.extraHours }}</span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-gray-600 text-xs">Response time</span>
                  <span class="font-semibold text-mumble-accent text-xs">{{ summaryData.responseTime }}</span>
                </div>
                <div v-if="selectedPackage && selectedPackage.name !== 'none'" class="flex gap-2 pt-2 border-t border-gray-100">
                  <span class="px-2 py-1 bg-mumble-accent/10 text-mumble-accent text-xs rounded">BugReporter</span>
                  <span class="px-2 py-1 bg-mumble-accent/10 text-mumble-accent text-xs rounded">AppSentinel</span>
                </div>

                <!-- Dettaglio piano mobile -->
                <div v-if="selectedPackage" class="pt-2 border-t border-gray-100 mt-2">
                  <div class="bg-blue-50 rounded-lg p-3 border border-blue-100">
                    <p class="text-xs text-gray-700 leading-relaxed">
                      <template v-if="selectedPackage.name === 'none'">
                        Non include manutenzione preventiva, SLA o garanzie sul downtime.
                      </template>
                      <template v-else-if="selectedPackage.name === 'essential'">
                        Gestione di base, non indicato per prodotti in crescita.
                      </template>
                      <template v-else-if="selectedPackage.name === 'growth'">
                        Evoluzione del prodotto, senza SLA dedicati sul downtime.
                      </template>
                      <template v-else-if="selectedPackage.name === 'full'">
                        Copre continuità, sicurezza ed evoluzione. Escluse major release.
                      </template>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <footer class="mt-12 md:mt-20 pt-6 md:pt-8 border-t border-gray-100">
        <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 text-xs text-gray-500">
          <!-- Contatti -->
          <div class="flex flex-col sm:flex-row gap-4">
            <a href="mailto:hello@mumble.it" class="hover:text-mumble-accent transition-colors">
              hello@mumble.it
            </a>
            <span class="hidden sm:inline text-gray-300">·</span>
            <a href="tel:+390595551256" class="hover:text-mumble-accent transition-colors">
              +39 059 551256
            </a>
          </div>

          <!-- Info aziendali -->
          <div class="flex flex-col md:items-end leading-relaxed">
            <p>© 2024 Mumble® srl · P.Iva 03525740365</p>
            <p class="text-gray-400">Nr. REA MO-118019 · Capitale sociale 150.000,00 Euro i.v.</p>
          </div>
        </div>
      </footer>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { track } from '@vercel/analytics';
import QuizFinder from './QuizFinder.vue';

interface Package {
  name: string;
  displayName: string;
  baseCost: number;
  hourlyRate: number;
  includedHours: number;
  responseTime: string;
}

// Helper to get device type
const getDeviceType = (): string => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Helper to get tracking metadata
const getTrackingMetadata = () => ({
  device_type: getDeviceType(),
  screen_width: window.innerWidth,
  screen_height: window.innerHeight,
  user_agent: navigator.userAgent,
  language: navigator.language,
  timestamp: new Date().toISOString()
});

export default defineComponent({
  name: 'AMSConfigurator',
  components: {
    QuizFinder
  },
  setup() {
    const packages: Record<string, Package> = {
      none: {
        name: 'none',
        displayName: 'Pay as you go',
        baseCost: 0,
        hourlyRate: 100,
        includedHours: 0,
        responseTime: ''
      },
      essential: {
        name: 'essential',
        displayName: 'Essential Care',
        baseCost: 2400,
        hourlyRate: 85,
        includedHours: 8,
        responseTime: '16h'
      },
      growth: {
        name: 'growth',
        displayName: 'Growth Care',
        baseCost: 6000,
        hourlyRate: 80,
        includedHours: 40,
        responseTime: '8h'
      },
      full: {
        name: 'full',
        displayName: 'Full Care',
        baseCost: 12000,
        hourlyRate: 75,
        includedHours: 80,
        responseTime: '4h'
      }
    };

    const selectedPackage = ref<Package | null>(null);
    const totalHours = ref(0);
    const showMobileSummary = ref(false);

    const selectPackage = (pkg: Package) => {
      const previousPackage = selectedPackage.value;
      selectedPackage.value = pkg;

      // Keep user's selected hours when switching packages, but ensure minimum
      if (!previousPackage) {
        totalHours.value = pkg.includedHours;
      } else {
        totalHours.value = Math.max(totalHours.value, pkg.includedHours);
      }

      // Track package selection
      track('package_selected', {
        package_name: pkg.name,
        package_display_name: pkg.displayName,
        package_base_cost: pkg.baseCost,
        package_hourly_rate: pkg.hourlyRate,
        included_hours: pkg.includedHours,
        response_time: pkg.responseTime,
        ...getTrackingMetadata()
      });
    };

    // Track hours adjustment with debounce
    let hoursDebounceTimer: number;
    watch(totalHours, (newHours, oldHours) => {
      if (selectedPackage.value && oldHours !== undefined && newHours !== oldHours) {
        clearTimeout(hoursDebounceTimer);
        hoursDebounceTimer = setTimeout(() => {
          const pkg = selectedPackage.value;
          if (!pkg) return;

          const extraHours = Math.max(0, newHours - pkg.includedHours);
          const totalCost = pkg.name === 'none'
            ? newHours * pkg.hourlyRate
            : pkg.baseCost + (extraHours * pkg.hourlyRate);

          track('hours_adjusted', {
            package_name: pkg.name,
            total_hours: newHours,
            included_hours: pkg.includedHours,
            extra_hours: extraHours,
            total_cost: totalCost,
            workdays: Math.floor(newHours / 8),
            ...getTrackingMetadata()
          });
        }, 1000); // Debounce for 1 second
      }
    });

    // Track mobile summary toggle
    watch(showMobileSummary, (isOpen) => {
      track('mobile_summary_toggled', {
        is_open: isOpen,
        has_package_selected: selectedPackage.value !== null,
        package_name: selectedPackage.value?.name || 'none',
        ...getTrackingMetadata()
      });
    });

    const summaryData = computed(() => {
      if (!selectedPackage.value) {
        return {
          packageName: 'Nessuno',
          includedHours: '0',
          extraHours: '0',
          responseTime: 'N/A',
          total: '€0'
        };
      }

      const pkg = selectedPackage.value;

      if (pkg.name === 'none') {
        const total = totalHours.value * pkg.hourlyRate;
        return {
          packageName: pkg.displayName,
          includedHours: '0 ore',
          extraHours: `${totalHours.value} ore`,
          responseTime: 'Nessuna garanzia',
          total: `€${total.toLocaleString('it-IT')}`
        };
      }

      const extraHours = Math.max(0, totalHours.value - pkg.includedHours);
      const total = pkg.baseCost + (extraHours * pkg.hourlyRate);

      return {
        packageName: pkg.displayName,
        includedHours: `${pkg.includedHours} ore`,
        extraHours: `${extraHours} ore`,
        responseTime: `${pkg.responseTime}`,
        total: `€${total.toLocaleString('it-IT')}`
      };
    });

    return {
      packages,
      selectedPackage,
      totalHours,
      showMobileSummary,
      selectPackage,
      summaryData
    };
  }
});
</script>

<style scoped>
/* Hide number input arrows */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f28c73;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(242, 140, 115, 0.4);
  transition: all 0.2s;
}

.slider::-webkit-slider-thumb:hover {
  transform: scale(1.1);
  box-shadow: 0 3px 10px rgba(242, 140, 115, 0.6);
}

.slider::-moz-range-thumb {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f28c73;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 8px rgba(242, 140, 115, 0.4);
  transition: all 0.2s;
}

.slider::-moz-range-thumb:hover {
  transform: scale(1.1);
  box-shadow: 0 3px 10px rgba(242, 140, 115, 0.6);
}

/* Mobile: larger touch target */
@media (max-width: 768px) {
  .slider::-webkit-slider-thumb {
    width: 28px;
    height: 28px;
  }
  
  .slider::-moz-range-thumb {
    width: 28px;
    height: 28px;
  }
}
</style>
